import { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { Add, Close, Work } from '@mui/icons-material';

// Components
import { Modal, Loader } from '../../../components';
import {
  Fade,
  ButtonGroup,
  Button,
  Grid,
  TextField,
  Box,
  Divider,
  Card as CardMui,
  CardContent,
  CardHeader,
  Paper
} from '@mui/material';
// models
// fetch
import { useSnackbar } from 'notistack';
import { ICreateNewPossessionPayload } from '../../../models/possessions';
import { createPossession } from '../../../fetch/possessions';
import { useHistory } from 'react-router-dom';
import { PossessionType } from './manage-model-home-furniture';

interface IAddPossessionModal {
  possessionType: number;
  open: boolean;
  modalIsLoading: boolean;
  onClose: () => void;
}

const AddNewPossessionSchema = Yup.object().shape({
  code: Yup.string(),
  description: Yup.string().required('Required')
});

export const AddPossessionModal: FC<IAddPossessionModal> = ({
  possessionType,
  open,
  modalIsLoading,
  onClose
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const isModelHomeFurniture = possessionType === PossessionType.ModelHomeFurniture;

  const getTitle = () => {
    if (possessionType === PossessionType.ModelHomeFurniture) {
      return 'Model Home Furniture';
    }
    if (possessionType === PossessionType.Tools) {
      return 'Tool';
    }

    return 'Possession';
  };
  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          code: '',
          description: ''
        }}
        validationSchema={AddNewPossessionSchema}
        onSubmit={async (values, actions) => {
          const data: ICreateNewPossessionPayload = {
            ...values,
            possessionType: possessionType
          };

          try {
            const possessionId = await createPossession(data);
            // Navigate to details page
            const route =
              data.possessionType === PossessionType.Tools
                ? 'manage-tools'
                : 'manage-model-home-furniture';
            history.push(`/development/possessions/${route}/${possessionId}`);
            enqueueSnackbar(`Successfully added ${getTitle()}.`, {
              variant: 'success'
            });
            onClose();
            actions.resetForm();
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? `Error adding ${getTitle()}.`, {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                  resetForm();
                }
              }}
              maxWidth="sm"
              title={`Add New ${getTitle()}`}
            >
              {(modalIsLoading || isSubmitting) && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <CardMui elevation={0} variant="outlined" className={classes.marginBottom}>
                        <CardHeader
                          avatar={<Work />}
                          title="INFO"
                          className={classes.primaryHeader}
                        />
                        <CardContent className={classes.cardContent}>
                          <Paper elevation={0} square>
                            <div className={classes.content}>
                              <Grid container spacing={1}>
                                {isModelHomeFurniture && (
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      required={isModelHomeFurniture ? true : false}
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Code"
                                      name="name"
                                      size="small"
                                      value={values.code}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('code', e.target.value)}
                                      error={!!(touched?.code && errors?.code)}
                                    />
                                  </Grid>
                                )}
                                <Grid item xs={12} sm={isModelHomeFurniture ? 6 : 12}>
                                  <TextField
                                    required
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Description"
                                    name="description"
                                    size="small"
                                    value={values.description}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('description', e.target.value)}
                                    error={!!(touched?.description && errors?.description)}
                                  />
                                </Grid>
                              </Grid>
                            </div>
                            <Box mt={1} mb={0.5}>
                              <Divider />
                            </Box>
                            <ButtonGroup>
                              <Button
                                disabled={
                                  !dirty ||
                                  isSubmitting ||
                                  !isValid ||
                                  (isModelHomeFurniture && !values.code)
                                }
                                type="submit"
                                startIcon={<Add />}
                                variant="contained"
                                color="primary"
                              >
                                {'Add'}
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="inherit"
                                startIcon={<Close />}
                                onClick={() => {
                                  if (!deepEqual(initialValues, values)) {
                                    const result = window.confirm(
                                      'You have unsaved changes, are you sure you want to exit?'
                                    );
                                    if (result) {
                                      resetForm();
                                      onClose();
                                    } else {
                                      return;
                                    }
                                  } else {
                                    onClose();
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                            </ButtonGroup>
                          </Paper>
                        </CardContent>
                      </CardMui>
                    </Grid>
                  </Grid>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-of-type)': {
      marginTop: theme.spacing(1)
    }
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  content: {
    marginTop: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  }
}));
